import React, { useState } from 'react';
import { formatPrice } from '../../../../../rbts/helpers';
import {
  getWalletTicketByFolioService,
  traspasarWalletAFisicoService,
} from '../../../../service/tickets/Tickets';

type WalletInfo = {
  folio: string;
  saldo: number;
  nombre: string | null;
};

type Resultado = {
  folioWallet: string;
  folioFisico: string;
  monto: number;
  nuevoSaldoWallet: number;
};

const View: React.FC = () => {
  const [folioWallet, setFolioWallet] = useState<string>('');
  const [buscandoWallet, setBuscandoWallet] = useState<boolean>(false);
  const [walletInfo, setWalletInfo] = useState<WalletInfo | null>(null);
  const [errorWallet, setErrorWallet] = useState<string | null>(null);

  const [folioFisico, setFolioFisico] = useState<string>('');
  const [monto, setMonto] = useState<string>('');
  const [traspasando, setTraspasando] = useState<boolean>(false);
  const [resultado, setResultado] = useState<Resultado | null>(null);
  const [errorTraspaso, setErrorTraspaso] = useState<string | null>(null);

  const resetTransferState = () => {
    setResultado(null);
    setErrorTraspaso(null);
  };

  const handleBuscarWallet = async () => {
    if (!folioWallet.trim()) return;
    setBuscandoWallet(true);
    setWalletInfo(null);
    setErrorWallet(null);
    setFolioFisico('');
    setMonto('');
    resetTransferState();
    try {
      const data = await getWalletTicketByFolioService(folioWallet.trim());
      setWalletInfo(data);
    } catch (err: any) {
      const msg =
        err?.response?.data?.doc?.message ||
        'No se encontró el folio Wallet o se encuentra inactivo.';
      setErrorWallet(msg);
    } finally {
      setBuscandoWallet(false);
    }
  };

  const handleTraspasar = async () => {
    if (!walletInfo || !folioFisico.trim() || !monto) return;
    const montoNum = Number(monto);
    if (montoNum <= 0 || montoNum > walletInfo.saldo) return;

    setTraspasando(true);
    resetTransferState();
    try {
      const data = await traspasarWalletAFisicoService(
        folioWallet.trim(),
        folioFisico.trim(),
        montoNum
      );
      setResultado(data);
      setFolioFisico('');
      setMonto('');
      setWalletInfo({ ...walletInfo, saldo: data.nuevoSaldoWallet });
    } catch (err: any) {
      const msg =
        err?.response?.data?.doc?.message ||
        'Ocurrió un error al realizar el traspaso. Intenta de nuevo.';
      setErrorTraspaso(msg);
    } finally {
      setTraspasando(false);
    }
  };

  const montoNum = Number(monto);
  const montoExcedeSaldo = walletInfo !== null && monto !== '' && montoNum > walletInfo.saldo;
  const montoValido =
    monto !== '' && !isNaN(montoNum) && montoNum > 0 && walletInfo !== null && montoNum <= walletInfo.saldo;
  const puedeTranspasar = walletInfo !== null && folioFisico.trim() !== '' && montoValido && !traspasando;

  return (
    <div className="card">
      <div className="card-header border-0 pt-6">
        <div className="card-title">
          <h3 className="card-label fw-bolder text-dark">Traspaso de Saldo Wallet → Folio Físico</h3>
        </div>
      </div>
      <div className="card-body py-3">

        {/* Paso 1: Buscar Folio Wallet */}
        <div className="mb-8">
          <h5 className="fw-bold text-primary mb-4">1. Buscar Folio Wallet</h5>
          <div className="row align-items-end g-4">
            <div className="col-md-4">
              <label className="form-label fw-bold">Folio Wallet (inicia con "D")</label>
              <input
                type="text"
                className="form-control"
                placeholder='Ej: D12345'
                value={folioWallet}
                onChange={(e) => {
                  setFolioWallet(e.target.value);
                  setWalletInfo(null);
                  setErrorWallet(null);
                  resetTransferState();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleBuscarWallet();
                }}
              />
            </div>
            <div className="col-md-2">
              <button
                className="btn btn-primary w-100"
                onClick={handleBuscarWallet}
                disabled={!folioWallet.trim() || buscandoWallet}
              >
                {buscandoWallet ? (
                  <>
                    Buscando...
                    <span className="spinner-border spinner-border-sm ms-2"></span>
                  </>
                ) : (
                  <>
                    <i className="bi bi-search me-1"></i> Buscar
                  </>
                )}
              </button>
            </div>

            {walletInfo && (
              <div className="col-md-6">
                <div className="alert alert-success py-3 mb-0">
                  {walletInfo.nombre && (
                    <div>
                      <strong>Titular:</strong> {walletInfo.nombre}
                    </div>
                  )}
                  <div>
                    <strong>Folio:</strong> {walletInfo.folio}&nbsp;|&nbsp;
                    <strong>Saldo disponible:</strong>{' '}
                    {formatPrice(walletInfo.saldo, 'es-MX', 'MXN', 2, 2)}
                  </div>
                </div>
              </div>
            )}

            {errorWallet && (
              <div className="col-md-6">
                <div className="alert alert-danger py-3 mb-0">{errorWallet}</div>
              </div>
            )}
          </div>
        </div>

        {/* Paso 2: Ejecutar Traspaso */}
        {walletInfo && (
          <div className="mb-6">
            <h5 className="fw-bold text-primary mb-4">2. Ejecutar Traspaso</h5>
            <div className="row g-4 mb-4">
              <div className="col-md-4">
                <label className="form-label fw-bold">Folio Físico destino</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={10}
                  className="form-control"
                  placeholder="Ej: 20001"
                  value={folioFisico}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '');
                    setFolioFisico(val);
                    resetTransferState();
                  }}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label fw-bold">
                  Monto a traspasar&nbsp;
                  <span className="text-muted fs-7">
                    (máx. {formatPrice(walletInfo.saldo, 'es-MX', 'MXN', 2, 2)})
                  </span>
                </label>
                <input
                  type="number"
                  className={`form-control ${montoExcedeSaldo ? 'is-invalid' : ''}`}
                  placeholder="Ej: 100.00"
                  value={monto}
                  min={0.01}
                  max={walletInfo.saldo}
                  step={0.01}
                  onChange={(e) => {
                    setMonto(e.target.value);
                    resetTransferState();
                  }}
                />
                {montoExcedeSaldo && (
                  <div className="invalid-feedback">El monto excede el saldo disponible.</div>
                )}
              </div>
              <div className="col-md-4 d-flex align-items-end">
                <button
                  className="btn btn-primary w-100"
                  onClick={handleTraspasar}
                  disabled={!puedeTranspasar}
                >
                  {traspasando ? (
                    <>
                      Traspasando...
                      <span className="spinner-border spinner-border-sm ms-2"></span>
                    </>
                  ) : (
                    <>
                      <i className="bi bi-arrow-left-right me-2"></i>
                      Traspasar
                    </>
                  )}
                </button>
              </div>
            </div>

            {resultado && (
              <div className="alert alert-success">
                <div className="fw-bold mb-1">Traspaso realizado exitosamente</div>
                <div>
                  <span>
                    Folio Wallet: <strong>{resultado.folioWallet}</strong>
                  </span>
                  &nbsp;→&nbsp;
                  <span>
                    Folio Físico: <strong>{resultado.folioFisico}</strong>
                  </span>
                  &nbsp;|&nbsp;
                  <span>
                    Monto: <strong>{formatPrice(resultado.monto, 'es-MX', 'MXN', 2, 2)}</strong>
                  </span>
                </div>
                <div className="text-muted fs-7 mt-1">
                  Nuevo saldo Wallet:{' '}
                  {formatPrice(resultado.nuevoSaldoWallet, 'es-MX', 'MXN', 2, 2)}
                </div>
              </div>
            )}

            {errorTraspaso && (
              <div className="alert alert-danger">{errorTraspaso}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export { View };
